import { getCascadeFlatData, getDefaultActiveList } from '@gp/utils/project';
import { isEmpty } from 'lodash-es';

// 以level升序排列
export const groupCascadeOptionByLevel = (arr: any) => {
  if (!Array.isArray(arr) || !arr?.length) return [];
  // 使用reduce按level分组
  const grouped = arr?.reduce((acc: any, obj: any) => {
    const { level } = obj;
    if (!acc[level]) {
      acc[level] = [];
    }
    acc[level].push(obj);
    return acc;
  }, {});
  // 将分组后的对象转为数组并按level升序排序
  return Object.entries(grouped)
    ?.sort(([a], [b]) => a - b)
    ?.map(([_, items]) => items);
};

export const sortArrByStats = (treeData: any) => {
  if (!Array.isArray(treeData)) return treeData;
  const arrTemp = [...treeData] || [];
  return arrTemp
    ?.sort((a, b) => {
      const priorityStates = ['all', 'partial'];
      const aPriority = priorityStates.includes(a.state);
      const bPriority = priorityStates.includes(b.state);
      if (aPriority && !bPriority) return -1;
      if (!aPriority && bPriority) return 1;
      return 0;
    })
    ?.map((item: any) => {
      if (item?.child?.length) {
        return {
          ...item,
          child: sortArrByStats(item?.child),
        };
      }
      return item;
    });
};

/**
 * 标记四级级联数据勾选状态
 * @param {Array} treeData 原始树形数据
 * @param {Array} selectedValues 最后一级选中值数组
 * @returns {Array} 带状态标记的完整树
 */
export const markCascadeStatus = (treeData: any, selectedValues: any) => {
  const traverse = (nodes: any) => {
    nodes?.forEach((node: any) => {
      if (node?.child?.length) {
        traverse(node.child);

        // 计算子节点状态
        const childStates = node?.child?.map((c: any) => c.state);
        if (childStates?.every((s: any) => s === 'all')) {
          node.state = 'all';
        } else if (childStates?.some((s: any) => s !== 'none')) {
          node.state = 'partial';
        } else {
          node.state = 'none';
        }
      } else {
        // 叶子节点直接判断选中状态
        node.state = selectedValues?.includes(node.id) ? 'all' : 'none';
      }
    });
  };
  traverse(treeData);
  if (!Array.isArray(treeData) || treeData.length === 0) return [];
  return treeData;
};

/**
 * 标记四级级联数据选中状态
 * @param {Array} treeData 原始树形数据
 * @param {Array} selectedValues 最后一级选中值数组
 * @returns {Array} 带状态标记的完整树
 */
export const markCascadeActiveStatus = (treeData: any, clickItem: any) => {
  // 已选不触发
  if (clickItem.active) return treeData;

  const traverse = (nodes: any, parentNode: any) => {
    let isFilterFirstChild = false;
    nodes?.forEach((node: any) => {
      // 如果是本节点同层次
      if (node?.level === clickItem?.level) {
        if (node?.id === clickItem?.id) {
          node.active = true;
        } else {
          node.active = false;
        }
      }
      // 如果parentNode不为空，则需要计算子级选中状态
      // 如果是子级,并且还没碰到第一个
      if (node?.level > clickItem?.level && !isEmpty(parentNode)) {
        if (
          node?.parentId === parentNode?.id &&
          !isFilterFirstChild &&
          !node.isEndPanel &&
          parentNode?.active === true
        ) {
          node.active = true;
          isFilterFirstChild = true;
        } else {
          node.active = false;
        }
      }
      if (node.child?.length) {
        traverse(node.child, node);
      }
    });
  };
  traverse(treeData, {});
  if (!Array.isArray(treeData) || treeData.length === 0) return [];
  return treeData;
};

/**
 * 获取最后一层子节点ID
 * @param {TreeNode} node - 起始节点
 * @returns {string[]} 叶子节点ID数组
 */
export const findNodeById = (tree: any, targetId: any) => {
  if (tree.id === targetId) return tree;
  if (tree.child) {
    for (const child of tree.child) {
      const found = findNodeById(child, targetId);
      if (found) return found;
    }
  }
  return null;
};

export const findNodeIteratively = (data: any, targetId: any) => {
  const stack = [...data];
  while (stack.length) {
    const node = stack?.pop();
    if (node.id === targetId) return node;
    if (node.child) stack?.push(...node?.child?.reverse());
  }
  return null;
};

export const getLastLevelIds = (node: any) => {
  if (!node) return [];
  if (node?.isEndPanel) return [node.id];
  return (node.child || [])?.reduce((acc: any, child: any) => [...acc, ...getLastLevelIds(child)], []);
};

// 转换为本级联筛选器所需数据结构
export const getFormOptions = (optionsData: any, value: any) => {
  const optionsTemp = [...optionsData];
  const resultList = markCascadeStatus(optionsTemp, value);
  if (!Array.isArray(resultList) || resultList.length === 0) return [];
  const flatDatas = getCascadeFlatData(resultList);
  const flatDatasTemp = groupCascadeOptionByLevel(flatDatas);
  return flatDatasTemp;
};

// 获取当前node下所有最后一级id数据
export const getAllIdsByNode = (options: any, nodeItem: any) => {
  const nodeDatas = options?.flatMap((item: any) => findNodeById(item, nodeItem.id))?.filter(Boolean)[0];
  const newIds = getLastLevelIds(nodeDatas);
  return newIds;
};

// 清空级联筛选器state与active
export const resetCascadeState = (data: any) => {
  if (Array.isArray(data)) {
    return data?.map((item: any) => ({
      ...item,
      state: 'none',
      active: false,
      child: resetCascadeState(item.child),
    }));
  }
  return {
    ...data,
    state: 'none',
    active: false,
    child: resetCascadeState(data.child),
  };
};

// 搜索，获取过滤
export const getSearchLevelDatas = (options: any, keyword: any) => {
  const optionsTemp = [...options];

  const lowerKeyword = keyword?.toLowerCase();
  return optionsTemp?.reduce((results: any, item: any) => {
    // 检查当前节点是否匹配
    const isMatch = item?.name?.toLowerCase()?.includes(lowerKeyword);
    const itemTemp = item;

    // 如果有子节点则递归处理
    let children = [];
    if (itemTemp?.child && itemTemp?.child?.length) {
      children = getSearchLevelDatas(itemTemp?.child, keyword);
    }

    // 当前节点或子节点匹配则保留
    if (isMatch || children.length) {
      results.push({
        ...itemTemp,
        child: children.length ? children : itemTemp.child,
      });
    }

    return results;
  }, []);
};

// 设置默认第一个高亮
export const setOptionsFirstActive = (data: any, parent = {}) => {
  if (Array.isArray(data)) {
    return data?.map((item: any, index: number) => {
      const itemTemp = {
        ...item,
        active: getDefaultActiveList(item?.isEndPanel, index, parent),
      } as any;

      return {
        ...itemTemp,
        child: setOptionsFirstActive(item.child, itemTemp),
      };
    });
  }
  return data;
};
